import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Heart, Users, Gift, Gem, Briefcase, Church, Plane, Landmark,
  PartyPopper, ArrowRight, ShieldCheck, Music, MapPin,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.07, duration: 0.5 } }),
};

const cases = [
  {
    icon: Users, title: "Parents & Children",
    img: "https://images.unsplash.com/photo-1595981685360-02d21ed6afde?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    desc: "Send your kid on a hunt through their own life. Where were you born? Where was this ridiculous photo taken? Where did you learn to drive?",
    example: "\"Growing Up\" — 8 clues, one every Sunday, ending at the old house.",
  },
  {
    icon: Heart, title: "Couples",
    img: "https://images.unsplash.com/photo-1779723204737-189eae74ff14?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    desc: "Where we met. First date. First kiss. The bench, the bar, the beach. Rebuild your whole story as a trail of pins.",
    example: "\"Our First Year\" — 12 locations, each unlocking a photo you forgot existed.",
  },
  {
    icon: Gem, title: "Proposals",
    img: "https://images.unsplash.com/photo-1522673607200-164d1b6ce486?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    desc: "The ultimate quest: a romantic journey through every place that mattered, ending at one final pin — where you're waiting on one knee.",
    example: "Final clue unlocks: \"Turn around.\"",
  },
  {
    icon: Gift, title: "Birthdays",
    img: "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    desc: "A trail of meaningful spots leading to messages, embarrassing photos, and eventually — the actual gift.",
    example: "\"Your Life in Places\" — 5 clues, one gift card taped under a park bench.",
  },
  {
    icon: Briefcase, title: "Corporate & Team Building",
    img: "https://images.unsplash.com/photo-1592591281836-af5e5904c466?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    desc: "Split the office into teams and send them racing across downtown. Competitive, collaborative, and nobody's stuck doing trust falls.",
    example: "\"The Great Office Hunt\" — 6 checkpoints, winning team picks the happy hour spot.",
  },
  {
    icon: Church, title: "Church & Youth Groups",
    img: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    desc: "Guided, safe scavenger hunts for youth groups, scouts and schools — leaders set every location and see the whole trail before anyone plays.",
    example: "A history-of-our-parish quest ending at the potluck.",
  },
  {
    icon: Plane, title: "Travel & Road Trips",
    img: "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    desc: "Turn a vacation into a game, or relive one years later. Every stop becomes a checkpoint with the story attached.",
    example: "\"Route 66, Summer '24\" — replay the whole trip pin by pin.",
  },
  {
    icon: Landmark, title: "History & Tourism",
    img: "https://images.unsplash.com/photo-1552832230-c0197dd311b5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    desc: "Historic photos matched to real streets. Cities, museums and tourism boards can build quests through their own past.",
    example: "\"Jamestown, 1907\" — stand where the photographers stood.",
  },
  {
    icon: Music, title: "Celebrity Walk",
    img: "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    desc: "Trace the real geography behind legendary lives — homes, stages, studios and the streets where history happened.",
    example: "\"John Lennon: In My Life\" — 25 pins from Liverpool to the Dakota.",
  },
  {
    icon: PartyPopper, title: "Friends & Inside Jokes",
    img: "https://images.unsplash.com/photo-1511632765486-a01980e01a18?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    desc: "The concert. The diner at 3am. The spot where the canoe flipped. Some places only your group understands.",
    example: "One clue: a blurry photo. They'll know.",
  },
];

export default function UseCases() {
  return (
    <main data-testid="use-cases-page" className="pt-16">
      <section className="py-20 border-b border-[#2A3042] relative grain">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#FF453A] mb-5">Use cases</p>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white max-w-3xl">
            Whose story will<br />you hide?
          </h1>
          <p className="mt-6 text-lg text-[#9BA1B0] max-w-2xl leading-relaxed">
            The same engine powers a proposal, a birthday hunt, a corporate showdown
            and a walk through history.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cases.map((c, i) => (
            <motion.div
              key={c.title}
              variants={fadeUp} custom={i % 3} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="rounded-2xl bg-[#131620] border border-[#2A3042] hover:border-[#FF453A] transition-colors overflow-hidden flex flex-col group"
            >
              {c.img && (
                <div className="h-44 overflow-hidden">
                  <img src={c.img} alt={c.title} className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-opacity transition-transform duration-500" />
                </div>
              )}
              <div className="p-8 flex flex-col flex-1">
                <c.icon size={20} className="text-[#FF453A] mb-4" />
                <h3 className="font-heading font-semibold text-white text-xl mb-2">{c.title}</h3>
                <p className="text-sm text-[#9BA1B0] leading-relaxed flex-1">{c.desc}</p>
                <p className="mt-5 text-xs text-[#c9cdd8] bg-[#090A0F] border border-[#2A3042] rounded-xl px-4 py-3 italic">{c.example}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-20 bg-[#0b0d14] border-y border-[#2A3042]" data-testid="celebrity-walk-feature">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#FF453A] mb-4 flex items-center gap-2">
            <Music size={13} /> Featured Celebrity Walk
          </p>
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tighter text-white">
                John Lennon: In My Life
              </h2>
              <p className="mt-2 text-[#FF453A] font-heading font-semibold">Pinpoint the places that shaped a legend</p>
              <p className="mt-5 text-[#9BA1B0] leading-relaxed">
                Follow John Lennon from wartime Liverpool through the birth of The Beatles,
                Beatlemania, his solo years, and his final chapter in New York City. Solve
                each clue and drop your pin on the location.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                {["25 pins", "Liverpool", "Hamburg", "London", "New York", "Difficulty: Medium"].map((t) => (
                  <span key={t} className="rounded-full bg-[#131620] border border-[#2A3042] text-[#c9cdd8] text-xs font-semibold px-4 py-2">{t}</span>
                ))}
              </div>
              <p className="mt-6 text-sm text-[#5a6070]">
                Sample quest — the full 25-pin walk arrives when the PinQuests engine launches. Every location verified against historical sources.
              </p>
              <Link to="/play/lennon" data-testid="play-lennon-btn" className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#FF453A] hover:bg-[#FF6961] transition-colors text-white font-semibold px-7 py-3.5 glow-red">
                Play the first 3 pins free <ArrowRight size={16} />
              </Link>
            </div>
            <div className="rounded-2xl bg-[#131620] border border-[#2A3042] p-8">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#9BA1B0] mb-5">The trail begins…</p>
              {[
                { n: 1, t: "Lennon's Birthplace", c: "Liverpool" },
                { n: 5, t: "Where Lennon Met McCartney", c: "Liverpool" },
                { n: 13, t: "The Abbey Road Crossing", c: "London" },
                { n: 22, t: "The Imagine Mosaic", c: "New York" },
              ].map((p) => (
                <div key={p.n} className="flex items-center gap-4 py-3 border-b border-[#2A3042]/60 last:border-0">
                  <span className="w-8 h-8 shrink-0 rounded-full bg-[#FF453A]/10 border border-[#FF453A]/30 flex items-center justify-center font-heading font-bold text-[#FF453A] text-sm">{p.n}</span>
                  <div className="flex-1">
                    <p className="text-white text-sm font-medium">{p.t}</p>
                    <p className="text-xs text-[#5a6070]">{p.c}</p>
                  </div>
                  <MapPin size={15} className="text-[#5a6070]" />
                </div>
              ))}
              <p className="mt-4 text-xs text-[#5a6070] text-center">…and 21 more locations to hunt down.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 border-y border-[#2A3042]">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#34C759]/10 border border-[#34C759]/30 mb-6">
            <ShieldCheck size={24} className="text-[#34C759]" />
          </div>
          <h2 className="font-heading font-semibold text-3xl sm:text-4xl tracking-tight text-white">
            Public quests, verified first.
          </h2>
          <p className="mt-5 text-[#9BA1B0] leading-relaxed max-w-2xl mx-auto">
            Public city and history quests will only go live after review — locations are
            vetted so hunts never send anyone somewhere they shouldn't be. Private quests
            stay private: invite-only links, never discoverable. Parents and group leaders
            always see safety guidance before kids play.
          </p>
        </div>
      </section>

      <section className="py-24 text-center">
        <div className="max-w-2xl mx-auto px-5 sm:px-8">
          <h2 className="font-heading font-semibold text-3xl sm:text-4xl tracking-tight text-white">
            Got someone in mind already?
          </h2>
          <Link to="/early-access" data-testid="usecases-cta-btn" className="mt-9 inline-flex items-center gap-2 rounded-full bg-[#FF453A] hover:bg-[#FF6961] transition-colors text-white font-semibold px-8 py-4 glow-red">
            Get Early Access <ArrowRight size={17} />
          </Link>
        </div>
      </section>
    </main>
  );
}
